/**
 * copyFormatExecutor — `copy_format`, le pinceau : donner à une liste ou à une
 * plage de paragraphes la forme que montre un paragraphe modèle du même acte
 * (#1869, spec #1858).
 *
 * Les règles communes aux écritures de forme — écriture directe, cible résolue
 * au fichier exporté, relecture clôturée sur la révision, ce qui compte comme
 * une écriture — sont dites une fois, dans l'en-tête de `ecritureDeForme.ts`.
 * Ce que la copie reprend du modèle, ce qu'elle laisse et les mesures dont ces
 * règles viennent sont dans l'en-tête de `copieDeForme.ts` ; l'enchaînement
 * des écritures sur la liste des cibles, dans `travauxDeParagraphe.ts` ; le
 * reçu est décidé par `recuDEcritureDeForme.ts`, et ce qu'il dit de plus pour
 * une copie par `recuDeCopie.ts`.
 *
 * ## Deux manières d'écrire, dans le même appel
 *
 * - **La forme de texte et l'alignement partent en plan atomique** : UNE étape
 *   `format.apply` par paragraphe porte les deux, 200 étapes par plan, une
 *   entrée d'historique par plan. Tous les plans sont soumis à l'aperçu avant
 *   la première écriture. Une cible dont l'éditeur refuse l'étape, à l'aperçu
 *   ou à l'application, est SAUTÉE pour tout l'appel et nommée au reçu avec sa
 *   raison (`format.skipped`) ; les étapes qui écrivent une forme de texte là
 *   où l'éditeur risque de la refuser partent seules (`ecritureDeForme.ts`,
 *   « Quand l'éditeur refuse un lot »).
 * - **Le style nommé, les retraits, l'espacement et la bordure n'ont pas
 *   d'étape de plan** : une écriture par paragraphe pour chacun des trois
 *   premiers, une par côté de bordure à changer — sept au plus par paragraphe,
 *   chacune une entrée d'historique. Le reçu en donne le nombre
 *   (`format.writes`), et ce qui ne se défait plus au-delà de l'historique de
 *   l'éditeur (`format.writesNotUndoable`).
 *
 * Les plans passent d'abord : un plan ne peut pas suivre une écriture par
 * paragraphe (`ecritureDeForme.ts`, « Ce que la révision garde »). C'est aussi
 * pourquoi le style se pose sans toucher au texte (`copieDeForme.ts`).
 *
 * ## Une écriture interrompue
 *
 * Comme `format_paragraphs` : la série s'arrête au premier refus de l'éditeur,
 * ce qui est écrit le reste, et le reçu compte les paragraphes entièrement
 * écrits, ceux qui ne le sont qu'en partie et ceux qui ne le sont pas. Aucun
 * réglage n'y est dit conforme pour la demande entière.
 *
 * ## Hors périmètre
 *
 * Le modèle et les cibles sont des paragraphes du corps de l'acte, cellules de
 * tableau comprises (§16 de `superdoc-document-api.md`) : une cellule est copiée quand la cible la NOMME (`ids`) ;
 * une plage qui traverse un tableau laisse ses cellules (`table_cell_by_ids_only`, `PorteeDansLesTableaux` de
 * `cibleDeParagraphes.ts`). Le modèle nommé dans sa propre cible n'est pas écrit (compté `copy_source`).
 */

import { CANVAS_TEXT_FORMAT_SETTING_KEYS, type CanvasActionInput, type CanvasFormatSettingKey, type CanvasFormatUnreadableReason } from '@hector/shared-types';
import type { CanvasActionReceipt } from './actionReceipt';
import { resoudreLaCibleDeParagraphes, type ParagrapheCible } from './cibleDeParagraphes';
import { aQuelqueChoseACopier, cotesHorsCopie, ecrituresDeCopie, formeACopier, RETRAITS_COPIES, type EcrituresDeCopie } from './copieDeForme';
import { ecrireOuEpargnerLesRevisionsDeFormeDeParagraphe, ouvrirLEcritureDeForme } from './ecritureDeForme';
import { decrireLesNonResolus, direLesHorsDePortee, recuDeLEcritureDeForme } from './recuDEcritureDeForme';
import type { RaisonDeSaut } from './repliDeLot';
import type { CanvasActionContext } from './textActionExecutors';
import {
  compterLAvancement,
  direLAvancement,
  ecrireLaSerie,
  ecrireParPlans,
  ecritsSurUneRevisionDeParagraphe,
  ecrituresHorsPlanDuParagraphe,
  estEntier,
  type EcritureHorsPlan,
  type FamilleDeTravail,
  type SurfaceDeParagraphe,
  type SurfacesHorsPlan,
  type Travail,
} from './travauxDeParagraphe';
import type { CibleCopiee } from './verificationDeCopie';

type CopyFormatInput = Extract<CanvasActionInput, { action: 'copy_format' }>;

/** Les écritures par paragraphe qu'une copie emploie, mesurées présentes (`mesure-copy-format`, `mesure-format-paragraphs`). */
interface SurfacesDeCopie {
  styles?: { paragraph?: SurfacesHorsPlan['styles'] };
  format?: { paragraph?: SurfaceDeParagraphe };
}

const INTENTION = 'copy_format';

/** Les écritures qu'une cible peut recevoir : son étape de plan (texte et alignement), puis une par famille. */
type FamilleDeCopie = 'plan' | 'style' | 'retraits' | 'espacement' | 'bordure';

/** Une cible, ce qu'elle doit recevoir, et ce qu'elle a reçu. */
interface Copie extends Travail {
  ecritures: EcrituresDeCopie;
  faites: Set<FamilleDeCopie>;
}

/** Vrai si la cible a une étape de plan : une forme de texte, un alignement, ou les deux. */
const aUneEtapeDePlan = ({ ecritures }: Copie): boolean => ecritures.inline !== undefined || ecritures.alignement !== undefined;

const FAMILLES: ReadonlyArray<FamilleDeTravail<Copie> & { famille: FamilleDeCopie; nom: string }> = [
  { famille: 'plan', nom: 'text formatting and alignment', prevue: aUneEtapeDePlan },
  { famille: 'style', nom: 'named style', prevue: ({ ecritures }) => ecritures.style !== undefined },
  { famille: 'retraits', nom: 'indents', prevue: ({ ecritures }) => ecritures.retraits !== undefined },
  { famille: 'espacement', nom: 'spacing and line spacing', prevue: ({ ecritures }) => ecritures.espacement !== undefined },
  { famille: 'bordure', nom: 'border', prevue: ({ ecritures }) => ecritures.bordure !== undefined },
];

const aQuelqueChose = (travail: Copie): boolean => FAMILLES.some(({ prevue }) => prevue(travail));

/**
 * Copie la forme du paragraphe modèle sur les paragraphes que la cible
 * désigne, en écriture directe, puis relit le fichier exporté pour dire ce que
 * chaque cible montre réellement.
 *
 * Refusé avant toute écriture (`aborted`, cause nommée) tant que le modèle, la
 * cible, la lecture du fichier ou l'aperçu d'un plan fait défaut ; ensuite, le
 * reçu est décidé par `recuDeLEcritureDeForme` sur les faits relevés ici.
 */
export async function executeCopyFormat(ctx: CanvasActionContext, input: CopyFormatInput): Promise<CanvasActionReceipt> {
  // Les méthodes sont appelées SUR leur surface, jamais détachées : rien ne
  // garantit qu'une méthode du fournisseur reste liée une fois extraite.
  const doc = ctx.doc as CanvasActionContext['doc'] & SurfacesDeCopie;
  const [styles, paragraphe] = [doc.styles?.paragraph, doc.format?.paragraph];
  const ecriture = await ouvrirLEcritureDeForme(ctx, INTENTION, {
    'styles.paragraph.setStyleRef': styles?.setStyleRef,
    'format.paragraph.setIndentation': paragraphe?.setIndentation,
    'format.paragraph.setSpacing': paragraphe?.setSpacing,
    'format.paragraph.setBorder': paragraphe?.setBorder,
    'format.paragraph.clearBorder': paragraphe?.clearBorder,
  });
  if ('refus' in ecriture) return ecriture.refus;
  const { acte, refuser } = ecriture;

  // ── Le modèle : un paragraphe du corps de CET acte, cellule de tableau comprise.
  const source = resoudreLaCibleDeParagraphes(acte, 'cellules_par_identifiants', { kind: 'ids', nodeIds: [input.from] });
  const modele = source.paragraphes[0];
  if (modele === undefined) {
    return refuser({
      code: 'FORMAT_SOURCE_NOT_FOUND',
      message: `copy_format was refused before any write: model paragraph ${decrireLesNonResolus(source.nonResolus)}. `
        + 'Nothing was written. "from" must be the nodeId of a paragraph of this document (a table cell included), published by a read of it.',
    });
  }
  const forme = formeACopier(modele);
  if (!aQuelqueChoseACopier(forme)) {
    return refuser({
      code: 'FORMAT_SOURCE_UNREADABLE',
      message: `copy_format was refused before any write: nothing of the form of model paragraph ${modele.nodeId} can be read from the document file `
        + `(${forme.nonCopies.map(({ setting, reason }) => `${setting}: ${reason}`).join(', ')}), and a form that cannot be read is never guessed. `
        + 'Nothing was written. Read the form of the model (canvas_inspect formatOf) and set the wanted values with format_text and format_paragraphs.',
    });
  }

  // ── La cible : une liste ou une plage, le modèle écarté.
  const resolue = resoudreLaCibleDeParagraphes(acte, 'cellules_par_identifiants', input.target);
  if (input.target.kind === 'range' && resolue.nonResolus.length > 0) {
    return refuser({
      code: 'FORMAT_TARGET_NOT_FOUND',
      message: `copy_format was refused before any write: range bound ${decrireLesNonResolus(resolue.nonResolus)}. `
        + 'Nothing was written: a range with a missing bound is never guessed. Use two nodeIds of paragraphs published by a read of this document (table cells included).',
    });
  }
  // Une liste est rendue dans l'ordre demandé : les écritures, elles, suivent l'ordre du document.
  const cibles = resolue.paragraphes.filter((lu) => lu.nodeId !== modele.nodeId).sort((a, b) => a.rang - b.rang);
  const modeleDansLaCible = resolue.paragraphes.length - cibles.length;
  if (cibles.length === 0) {
    const couvert = [
      ...resolue.omis.map(({ reason, count }) => `${count} ${reason}`),
      ...(resolue.cellules.laissees === 0 ? [] : [`${resolue.cellules.laissees} table-cell paragraph(s), left to their table (target their ids only when the lawyer asked for the tables themselves)`]),
      ...(modeleDansLaCible === 0 ? [] : ['the model paragraph itself']),
      ...(resolue.nonResolus.length === 0 ? [] : [`unresolved: ${decrireLesNonResolus(resolue.nonResolus)}`]),
    ];
    return refuser({
      code: 'FORMAT_TARGET_EMPTY',
      message: 'copy_format was refused before any write: the target designates no paragraph to write other than the model'
        + `${couvert.length === 0 ? '' : ` (${couvert.join('; ')})`}. Nothing was written. `
        + (direLesHorsDePortee('copy_format', resolue.omis, undefined) ?? 'Target paragraphs of the document body.'),
    });
  }

  // Un changement de forme de paragraphe en attente : la règle commune des verbes de forme (`ecritureDeForme.ts`).
  const { aEcrire: ecrivables, surRevision, epargnes } = ecrireOuEpargnerLesRevisionsDeFormeDeParagraphe(cibles);
  const prevus = ecrivables.map((lu): Copie => ({ paragraphe: lu, ecritures: ecrituresDeCopie(forme, modele, lu), faites: new Set(), appliquees: new Set() }));
  // Un paragraphe dont une écriture est écartée sort des comptes de paragraphes écrits : il est compté à part, et nommé.
  const laisses = prevus.filter((prevu) => prevu.ecritures.empechees.length > 0);
  const gardes = prevus.filter((prevu) => prevu.ecritures.empechees.length === 0);

  // Une cible qui montre déjà la forme du modèle n'est pas réécrite.
  const travaux = prevus.filter(aQuelqueChose);
  const horsPlan = ({ paragraphe: cible, ecritures }: Copie): EcritureHorsPlan[] => ecrituresHorsPlanDuParagraphe({ styles, paragraphe }, cible, ecritures);

  // ── La forme de texte et l'alignement : UNE étape par cible (deux étapes s'y chevaucheraient), en plans
  // atomiques directs. La cible d'une étape que l'éditeur refuse est sautée pour tout l'appel, avec la raison à redire.
  const plans = await ecrireParPlans(ecriture, travaux.filter(aUneEtapeDePlan), {
    famille: 'plan',
    versEtape: ({ ecritures }) => ({
      ...(ecritures.inline === undefined ? {} : { inline: ecritures.inline }),
      ...(ecritures.alignement === undefined ? {} : { alignment: ecritures.alignement }),
    }),
    // Une étape d'alignement seul n'est pas de celles que l'éditeur risque de refuser pour le texte.
    ecritDuTexte: ({ ecritures }) => ecritures.inline !== undefined,
    nom: 'text formatting and alignment',
    etiquette: 'texte-et-alignement',
    dAutresEcrituresSuivent: travaux.some((travail) => horsPlan(travail).length > 0),
  });
  if ('refus' in plans) return plans.refus;

  // ── Le style nommé, les retraits, l'espacement, puis la bordure côté par côté, dans l'ordre du document.
  const serie = await ecrireLaSerie(ecriture, travaux, horsPlan);
  const cloture = await ecriture.clore();

  // Ce que l'éditeur refuse d'écrire, et ce qui est épargné d'avance, dans l'ordre du document.
  const sautes: Array<{ paragraphe: ParagrapheCible; raison: RaisonDeSaut }> = [
    ...epargnes.map((epargne) => ({ paragraphe: epargne, raison: { reason: 'pending_tracked_change' as const, epargne: true as const } })),
    ...prevus.flatMap((prevu) => (prevu.saute === undefined ? [] : [{ paragraphe: prevu.paragraphe, raison: prevu.saute }])),
  ].sort((a, b) => a.paragraphe.rang - b.paragraphe.rang);
  // Une cible sautée est comptée une fois, comme sautée : elle sort des cibles laissées, jugées et comptées écrites.
  const nonSautes = prevus.filter((prevu) => prevu.saute === undefined);
  const laissesEcrivables = laisses.filter((laisse) => laisse.saute === undefined);
  /** Ce que la copie n'a pas écrit exprès sur une cible, réglage par réglage : la relecture ne l'y juge pas. */
  const nonEcritsSur = ({ ecritures }: Copie): CibleCopiee['nonEcrits'] => {
    const nonEcrits: Partial<Record<CanvasFormatSettingKey, CanvasFormatUnreadableReason>> = {};
    for (const { reglages } of ecritures.empechees) for (const reglage of reglages) nonEcrits[reglage] = 'attributes_would_be_lost';
    if (ecritures.retraitsGardes) for (const reglage of RETRAITS_COPIES) nonEcrits[reglage] = 'list_numbering';
    if (ecritures.styleGarde) nonEcrits.styleId = 'list_numbering';
    if (ecritures.texteLaisse) for (const reglage of [...CANVAS_TEXT_FORMAT_SETTING_KEYS, 'otherTextFormatting'] as const) nonEcrits[reglage] = 'pending_tracked_change';
    return nonEcrits;
  };
  const nommer = (garde: (travail: Copie) => boolean): string[] => nonSautes.filter(garde).map((travail) => travail.paragraphe.nodeId);
  /** Le nombre de cibles par chose relevée sur elles, dans l'ordre de première rencontre. */
  const compter = <Chose extends string>(choses: (ecritures: EcrituresDeCopie) => readonly Chose[]): Map<Chose, number> => {
    const comptes = new Map<Chose, number>();
    for (const { ecritures } of nonSautes) for (const chose of new Set(choses(ecritures))) comptes.set(chose, (comptes.get(chose) ?? 0) + 1);
    return comptes;
  };
  const reglagesCopies = [
    ...(forme.styleId === undefined ? [] : ['styleId']),
    ...Object.keys(forme.paragraphe),
    ...(forme.interligneEnPoints === undefined ? [] : ['lineSpacing']),
    ...(forme.traits === undefined ? [] : ['border']),
    ...Object.keys(forme.texte),
  ];
  // L'étape de plan n'écrit une forme de paragraphe que si elle porte un alignement ; les autres familles en écrivent toutes.
  const ecritsSurRevision = ecritsSurUneRevisionDeParagraphe(nonSautes, surRevision, ({ ecritures }, famille) => famille !== 'plan' || ecritures.alignement !== undefined);
  return recuDeLEcritureDeForme({
    verbe: INTENTION,
    reglages: Object.fromEntries(reglagesCopies.map((reglage) => [reglage, true as const])),
    copie: {
      modele: modele.nodeId,
      forme,
      // La relecture juge les cibles entièrement écrites et celles qui n'avaient rien à recevoir.
      cibles: nonSautes.filter((prevu) => estEntier(prevu, FAMILLES)).map((prevu) => ({ nodeId: prevu.paragraphe.nodeId, avecTexte: prevu.paragraphe.passages.length > 0, nonEcrits: nonEcritsSur(prevu) })),
      retraitsGardes: nommer(({ ecritures }) => ecritures.retraitsGardes),
      styleGarde: nommer(({ ecritures }) => ecritures.styleGarde),
      texteLaisse: nommer(({ ecritures }) => ecritures.texteLaisse),
      cotesHorsCopie: (['between', 'bar'] as const).filter((cote) => [modele, ...cibles].some((lu) => cotesHorsCopie(lu).includes(cote))),
      texteNonRetirable: [...compter((ecritures) => ecritures.texteNonRetirable)].map(([element, paragraphs]) => ({ element, paragraphs })),
      paragrapheGarde: [...compter((ecritures) => ecritures.paragrapheGarde)].map(([what, paragraphs]) => ({ what, paragraphs })),
    },
    cible: {
      paragraphes: gardes.filter((garde) => garde.saute === undefined).map((garde) => garde.paragraphe),
      omis: [
        ...resolue.omis,
        ...(modeleDansLaCible === 0 ? [] : [{ reason: 'copy_source' as const, count: modeleDansLaCible }]),
        ...(laissesEcrivables.length === 0 ? [] : [{ reason: 'attributes_would_be_lost' as const, count: laissesEcrivables.length }]),
      ],
      nonResolus: resolue.nonResolus,
      cellules: resolue.cellules,
    },
    // Les comptes de paragraphes écrits ne prennent que les cibles dont aucune écriture n'est écartée.
    ...compterLAvancement(travaux.filter((travail) => travail.ecritures.empechees.length === 0 && travail.saute === undefined), FAMILLES, direLAvancement(travaux, FAMILLES)),
    ecrituresPrevues: plans.plans + serie.prevues,
    ...(plans.ecritesSeules === 0 ? {} : { ecritesSeules: plans.ecritesSeules }),
    sautes: sautes.map(({ paragraphe: saute, raison }) => ({ nodeId: saute.nodeId, ...raison })),
    ...(ecritsSurRevision.length === 0 ? {} : { surRevisionDeParagraphe: ecritsSurRevision }),
    ...(laissesEcrivables.length === 0 ? {} : {
      laisses: laissesEcrivables.map((laisse) => ({
        nodeId: laisse.paragraphe.nodeId,
        reglages: laisse.ecritures.empechees.flatMap((empechee) => empechee.reglages),
        attributs: laisse.ecritures.empechees.flatMap((empechee) => empechee.attributs),
        ecrit: estEntier(laisse, FAMILLES),
      })),
    }),
    ...cloture,
  });
}
